<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { stripePaymentMethodId } from '@/lib/stripeBilling';
import { useBillingStore } from '@/stores/billing';
import { Badge, Button, LayoutGap } from '@dabble/quillui';
import type Stripe from 'stripe';
import { computed, ref } from 'vue';
import PaymentMethodDisplay from './PaymentMethodDisplay.vue';

const props = defineProps<{
  method: Stripe.PaymentMethod;
}>();

const defaulting = ref(false);
const deleting = ref(false);
const retrying = ref(false);

const billingStore = useBillingStore();
const { t } = useI18n();
const { showToast } = useToast();

const isDefault = computed(() => {
  const customer = billingStore.customer as Stripe.Customer | null;
  const raw = customer?.invoice_settings?.default_payment_method;
  return stripePaymentMethodId(raw) === props.method?.id;
});

const canRetry = computed(() => billingStore.isDelinquent && billingStore.latestOpenInvoice !== null);

const busy = computed(() => defaulting.value || deleting.value || retrying.value);

async function makeDefault() {
  defaulting.value = true;
  try {
    await billingStore.setDefaultPaymentMethod(props.method.id);
    await billingStore.fetchBillingOverview();
  } catch (error) {
    logError('Failed to set default payment method:', error);
  } finally {
    defaulting.value = false;
  }
}

async function remove() {
  deleting.value = true;
  try {
    await billingStore.removePaymentMethod(props.method.id);
    await billingStore.fetchBillingOverview();
  } catch (error) {
    logError('Failed to remove payment method:', error);
  } finally {
    deleting.value = false;
  }
}

async function retryWithThisMethod() {
  const invoice = billingStore.latestOpenInvoice;
  if (!invoice) return;
  retrying.value = true;
  try {
    if (!isDefault.value) {
      await billingStore.setDefaultPaymentMethod(props.method.id);
    }
    const updated = await billingStore.retryInvoicePayment(invoice.id);
    if (updated?.status === 'paid') {
      showToast('success', t('billing_payment_method_retry_success'));
    }
  } catch (error) {
    logError('Failed to retry invoice with payment method:', error);
  } finally {
    retrying.value = false;
  }
}
</script>

<template>
  <LayoutGap direction="row" vertical-align="center" size="small" class="payment-method-container">
    <PaymentMethodDisplay :method="method" />
    <Badge v-if="isDefault" variant="success">
      {{ t('billing_payment_method_default') }}
    </Badge>
    <Button v-else variant="secondary" finish="outlined" :disabled="busy" @click="makeDefault">
      {{ t('billing_payment_method_default_set') }}
    </Button>
    <Button
      v-if="canRetry"
      variant="primary"
      finish="outlined"
      icon-left="billing"
      :disabled="busy"
      @click="retryWithThisMethod"
    >
      {{ t('billing_payment_method_retry') }}
    </Button>
    <Button
      class="remove-button"
      variant="danger"
      finish="ghost"
      icon-only="trash"
      :disabled="busy"
      :aria-label="t('billing_payment_method_remove')"
      @click="remove"
    />
  </LayoutGap>
</template>

<style lang="scss" scoped>
.remove-button {
  margin-inline-start: auto;
}
</style>
